<template>

  <v-container fluid class="page page-provider-order-list">

    <!-- Breadcrumb & Actions -->
    <v-row class="mt-3">
      <v-col sm="8" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-arrow-top-right-thin-circle-outline</v-icon>
        <div>Submit Orders</div>
      </v-col>
      <v-col sm="4" align="right">
        <!-- (manage connected shipper accounts) -->
        <v-btn text color="amber" class="white--text" v-if="!requiresConfiguration && $root.activeUserHasRole('admin')" @click="goToProviderConnectionsPage">
          <v-icon class="mr-2" style="font-size:18px;">mdi-link</v-icon>
          Manage Shipper Accounts
        </v-btn>
      </v-col>
    </v-row>

    <!-- (data loading spinner) -->
    <v-container class="mt-12 d-flex flex-column align-center justify-center" v-if="isDataLoading">
      <v-progress-circular indeterminate :size="50" style="margin-bottom:30px" color="primary" />
    </v-container>

    <!-- (data ready) -->
    <template v-else>
      <!---------------------------------------->
      <!-- Provider Connection Setup Required -->
      <!---------------------------------------->
      <template v-if="requiresConfiguration">
        <!-- Message about connecting to a provider (if no connections) -->
        <v-row class="mt-2" v-if="!hasProviderConnections">
          <v-col class="d-flex align-center justify-center">
            <div  class="message-connect-provider">
              <div class="message-heading">Connect to a Provider</div>
              <div style="color:#c9c9c9">to access orders to ship with {{ appName }}.</div>
              <v-btn text color="amber" style="margin-top:10px" class="white--text" @click="goToProviderConnectionsPage">
                + Add Provider Connection
              </v-btn>
            </div>
          </v-col>
        </v-row>
        <!-- Message about connecting to a provider (if no connections are active) -->
        <v-row class="mt-2" v-if="hasProviderConnections && !hasActiveProviderConnections">
          <v-col class="d-flex align-center justify-center">
            <div  class="message-connect-provider">
              <div class="message-heading">Activate a Provider</div>
              <div>to access orders to ship with {{ appName }}.</div>
              <v-btn text color="amber" style="margin-top:10px" class="white--text" @click="goToProviderConnectionsPage">
                + Manage Provider Connections
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </template>

      <v-container fluid class="px-0 mt-3" v-else>
        <!------------------------------------->
        <!-- Shipper Location Setup Required -->
        <!------------------------------------->
        <v-container class="notifications-container pt-4 pb-5 mb-5" :class="unapprovedShippers[0].shipper_status" v-if="hasUnapprovedShippers">
          <v-row>
            <v-col cols="12">
              <template v-if="unapprovedShippers[0].shipper_status === 'REQUIRES_APPROVAL'">
                <div class="d-flex align-center justify-center">
                  <v-icon style="font-size:30px;" class="mr-2" color="#FF9D29">mdi-check-bold</v-icon>
                  <span class="action-title">Shipper Account Ready to Submit</span>
                </div>
                <div class="action-info d-flex align-center justify-center mt-4 px-6">
                  <div align="center">Submit your shipper license changes to {{ appName }} for approval.</div>
                </div>
              </template>
              <template v-else-if="unapprovedShippers[0].shipper_status === 'PENDING_APPROVAL'">
                <div class="d-flex align-center justify-center">
                  <v-icon style="font-size:30px;" class="mr-2" color="#FFD600">mdi-clock-check-outline</v-icon>
                  <span class="action-title">Shipper Account Pending Approval</span>
                </div>
                <div class="action-info d-flex align-center justify-center mt-4 px-6">
                  <div align="center">License changes are being review by {{ appName }}.</div>
                </div>
              </template>
              <template v-else>
                <div class="d-flex align-center justify-center">
                  <v-icon style="font-size:30px;" class="mr-2" color="#FF5D67">mdi-alert-circle-outline</v-icon>
                  <span class="action-title">Shipper Account Not Configured</span>
                </div>
                <div class="action-info d-flex align-center justify-center mt-4 px-6">
                  <div align="center">Finish setting up your shipper account with a valid license and ship-to address to allow submitting orders.</div>
                </div>
              </template>
            </v-col>
          </v-row>
          <v-row v-if="unapprovedShippers[0].shipper_status === 'REQUIRES_APPROVAL' || unapprovedShippers[0].shipper_status === 'REQUIRES_SETUP'">
            <v-col cols="12" class="d-flex align-center justify-center">
              <router-link class="item-link" :to="generateShipperPageLink(unapprovedShippers[0])">
                <span v-if="unapprovedShippers[0].shipper_status === 'REQUIRES_APPROVAL'">Submit {{ unapprovedShippers[0].name }}</span>
                <span v-else>Set up {{ unapprovedShippers[0].name }}</span>
              </router-link>
            </v-col>
          </v-row>
        </v-container>

        <!-------------------------->
        <!-- Provider Order Lists -->
        <!-------------------------->
        <provider-order-list-data-table
            v-for="(info, index) in providerOrderList" :key="`list-${index}`"
            :connectionInfo="info" />
      </v-container>
    </template>

  </v-container>

</template>

<script>
import ProviderOrderListDataTable from '@/components/providerOrders/ProviderOrderListDataTable'

export default {
  name: 'ProviderOrderListPage',

  components: {
    ProviderOrderListDataTable
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      providerOrderList: [],
      unapprovedShippers: []
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    // Provider connections
    providerConnections () {
      return this.$store.getters['sh/data']('providerConnections')
    },
    providerConnectionItems () {
      return this.providerConnections ? this.providerConnections.items : []
    },
    hasProviderConnections () {
      return this.providerConnectionItems.length > 0
    },
    hasActiveProviderConnections () {
      return this.providerConnectionItems.filter(item => item.status === 'ACTIVE').length > 0
    },
    // Determines if confguration is required before provider orders can be accessed
    requiresConfiguration () {
      return !this.hasActiveProviderConnections
    },
    // Determines if unapproved shipper locations exist (to block shipping and show alert)
    hasUnapprovedShippers () {
      return this.unapprovedShippers.length > 0
    }
  },

  async beforeMount () {
    // Ensure provider connections are loaded
    await this.loadProviderConnections()

    // Prepare provider order lists (one per brand, per account)
    if (this.hasProviderConnections) {
      for (const connection of this.providerConnectionItems) {
        const enabledBrands = connection.settings.brands
        if (connection.status === 'ACTIVE') {
          if (connection.shipper.shipper_status !== 'APPROVED') this.unapprovedShippers.push(connection.shipper) // flag unapproved shippers
          if (enabledBrands && enabledBrands.length > 0) {
            for (const brandId of enabledBrands) {
              const brand = (connection.brands && connection.brands.length > 0)
                ? connection.brands.find(item => item.thirdparty_id == brandId) // eslint-disable-line eqeqeq
                : null
              const info = {
                name: (brand) ? brand.name : connection.name,
                brand_id: brandId,
                provider_alias: connection.provider_alias,
                account_id: connection.thirdparty_account_id,
                account: connection.account,
                shipper: connection.shipper,
                brand,
                settings: connection.settings,
                status: connection.status
              }
              this.providerOrderList.push(info)
            }
          } else {
            const info = {
              name: connection.name,
              provider_alias: connection.provider_alias,
              account_id: connection.thirdparty_account_id,
              account: connection.account,
              shipper: connection.shipper,
              settings: connection.settings,
              status: connection.status
            }
            this.providerOrderList.push(info)
          }
        }
      }
    }
  },

  methods: {
    goToProviderConnectionsPage () {
      this.$root.goTo('provider-connection-list')
    },

    // Generate link data for shipper location page (for a provided shipper item)
    generateShipperPageLink (item) {
      if (item && item.model) {
        return {
          name: 'shipper-item',
          params: { shipperId: item.id }
        }
      } else {
        return ''
      }
    },

    // Load all provider connections
    async loadProviderConnections (force) {
      this.isDataLoading = true
      this.dataLoadingError = null

      try {
        // Load all provider connections
        await this.$store.dispatch('sh/dataApi', {
          method: 'load',
          uri: 'provider-connections',
          opts: {
            resource: 'providerConnections',
            params: { with: 'shipper' },
            all: true,
            force
          }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style scoped>
  .message-connect-provider {
    font-size: 1.04rem;
    text-align: center;
    width: 420px;
    border: 1px solid #454545;
    border-radius: 4px;
    padding: 16px;
  }

  .message-connect-provider .message-heading {
    font-size: 1.3rem;
  }

  .message-set-location {
    font-size: 14px;
    text-align: center;
    width: 420px;
    border: 1px solid #454545;
    border-radius: 4px;
    padding: 16px;
  }

  .notifications-container.REQUIRES_SETUP {
    color: #FF5D67;
    border: 1px solid #FF5D67;
    border-radius: 4px;
  }

  .notifications-container.REQUIRES_APPROVAL {
    color: #FF9D29;
    border: 1px solid #FF9D29;
    border-radius: 4px;
  }

  .notifications-container.PENDING_APPROVAL {
    color: #FFD600;
    border: 1px solid #FFD600;
    border-radius: 4px;
  }

  .notifications-container .action-title {
    font-size: 1.18rem;
    font-weight: 500;
  }

  .notifications-container .action-info {
    font-size: 1.0rem;
    color: #9c9c9c;
  }

  .notifications-container a.item-link {
    text-decoration: none;
  }

  .notifications-container a.item-link:hover {
    text-decoration: underline;
  }
</style>
